import * as React from 'react';
import {
  Box, Typography, Stack, useMediaQuery, Divider,
} from '@mui/material';
import {
  ErrorBox, ErrorTitle, ErrorMessage, Link,
} from '@marageti/z4-lib';
import { GoogleLoginRequest, LoginRequest } from '@marageti/z4-sdk/lib/auth/Token';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import LoginDesktop from '../../assets/jpg/login-desktop.jpg';
import LoginMobile from '../../assets/jpg/login-mobile.jpg';
import './login.scss';
import ZSpinner from '../../components/z-lib/z-spinner';
import PasswordLogin from '../../components/auth/email-password-form';
import GoogleLoginView from '../../components/auth/google-sso-button';

type Props = {
  loading: boolean;
  loginError?: ApiError;
  onSubmit: (d: LoginRequest | GoogleLoginRequest) => void;
};

const View = ({
  loading,
  loginError = undefined,
  onSubmit,
}: Props): React.ReactElement => {
  const match = useMediaQuery('(min-width: 1100px) and (min-aspect-ratio: 1/1)');

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: match ? 'row-reverse' : 'column',
        alignItems: 'center',
        justifyContent: match ? 'center' : 'space-between',
      }}
    >
      <Box
        sx={{
          flex: 1,
          width: match ? 'fit-content' : '100%',
          overflow: 'hidden',
          height: '100%',
          maxHeight: { desktop: '75%' },
          ...match && {
            maxHeight: '100vh',
            maxWidth: '50%',
          },
        }}
      >
        <picture>
          <source
            height={1024}
            media="(min-width: 1100px)"
            srcSet={LoginDesktop}
            width={710}
          />
          <Box
            alt="Mountains"
            component="img"
            src={LoginMobile}
            sx={{
              minHeight: '100%',
              maxHeight: '100%',
              width: '100%',
              objectFit: 'cover',
            }}
          />
        </picture>
      </Box>
      <Box
        sx={{
          flex: 1,
          maxWidth: { desktop: '50%' },
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Stack
          spacing={3}
          sx={{
            width: '100%',
            maxWidth: '500px',
            padding: '0 24px',
          }}
          alignItems="center"
        >
          <Stack flexDirection="column" spacing="10px" alignItems="center">
            <Typography color="text.secondary" style={{ marginTop: '10px' }}>
              Please sign in to continue.
            </Typography>
          </Stack>
          {loading && (
          <ZSpinner />
          )}
          <Box
            display="flex"
            justifyContent="center"
          >
            <GoogleLoginView onSuccess={onSubmit} />
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Divider sx={{ flex: 1 }} />
            <Typography variant="body2" color="" margin="0 12px">or</Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>
          <PasswordLogin onSubmit={onSubmit} disablePasswordValidation mode="onSubmit" />
          <Link
            color="primary"
            sx={{ '&:hover': { color: 'secondary.main' } }}
            to="/reset"
            underline="always"
            variant="subtitle1"
          >
            Forgot Password?
          </Link>
          {loginError && (
            <ErrorBox>
              <ErrorTitle>
                There was an error logging in.
              </ErrorTitle>
              {loginError.code ? (
                <ErrorMessage>
                  {loginError.code && `Code: ${loginError.code} `}
                  {loginError.message || 'Please contact support'}
                  <br />
                  {loginError.id && `ID: ${loginError.id}`}
                </ErrorMessage>
              ) : (
                <ErrorMessage>
                  Please contact support.
                </ErrorMessage>
              )}
            </ErrorBox>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default View;
